import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLocaleContext } from "@/i18n/LocaleContext";
import { SUPPORTED_LOCALES, localePath, stripLocale } from "@/i18n/locales";
import { DOMAIN } from "@/config/app";

interface SeoHeadProps {
  title?: string;
  description?: string;
}

/**
 * Dynamically updates <head> metadata: title, description, canonical,
 * hreflang, and OG/Twitter tags.
 *
 * ARCHITECTURE NOTE (SPA limitation):
 * Social crawlers (Twitter, Facebook, LinkedIn) do NOT execute JavaScript.
 * They will only see the static fallback tags in index.html (English).
 * These dynamic updates benefit Googlebot (which renders JS) and in-app
 * navigation. For fully localized social previews, SSR or a pre-rendering
 * service would be required.
 */
export default function SeoHead({ title, description }: SeoHeadProps) {
  const locale = useLocaleContext();
  const location = useLocation();

  const pathWithoutLocale = stripLocale(location.pathname);

  useEffect(() => {
    // Set lang attribute
    document.documentElement.lang = locale;

    // Set title
    if (title) document.title = title;

    // Set meta description
    if (description) {
      setMeta('meta[name="description"]', "content", description);
    }

    // OG tags
    const canonicalUrl = `${DOMAIN}${localePath(locale, pathWithoutLocale)}`;
    if (title) {
      setMeta('meta[property="og:title"]', "content", title);
      setMeta('meta[name="twitter:title"]', "content", title);
    }
    if (description) {
      setMeta('meta[property="og:description"]', "content", description);
      setMeta('meta[name="twitter:description"]', "content", description);
    }
    setMeta('meta[property="og:url"]', "content", canonicalUrl);

    // Clean previous dynamic SEO links
    document.querySelectorAll("link[data-seo]").forEach((el) => el.remove());

    // Canonical
    const canonical = document.createElement("link");
    canonical.rel = "canonical";
    canonical.href = canonicalUrl;
    canonical.setAttribute("data-seo", "true");
    document.head.appendChild(canonical);

    // Hreflang for each locale
    for (const loc of SUPPORTED_LOCALES) {
      const link = document.createElement("link");
      link.rel = "alternate";
      link.hreflang = loc;
      link.href = `${DOMAIN}${localePath(loc, pathWithoutLocale)}`;
      link.setAttribute("data-seo", "true");
      document.head.appendChild(link);
    }

    // x-default → English
    const xdefault = document.createElement("link");
    xdefault.rel = "alternate";
    xdefault.hreflang = "x-default";
    xdefault.href = `${DOMAIN}${pathWithoutLocale}`;
    xdefault.setAttribute("data-seo", "true");
    document.head.appendChild(xdefault);

    return () => {
      document.querySelectorAll("link[data-seo]").forEach((el) => el.remove());
    };
  }, [locale, title, description, pathWithoutLocale]);

  return null;
}

/** Helper: find an existing meta tag and update its attribute value. */
function setMeta(selector: string, attr: string, value: string) {
  const el = document.querySelector(selector);
  if (el) el.setAttribute(attr, value);
}
